import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';
import { ChevronDown } from 'lucide-react';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const base = isLocal ? 'http://localhost:8080' : '';
  if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
  return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const SupportTicketsTab = ({ toast, setLightboxImage }) => {
  const { hasPermission } = useAuth();

  // Filters State
  const [ticketFilters, setTicketFilters] = useState({ status: 'ALL', startDate: '', endDate: '', ticketType: 'SUPPORT', search: '' });
  
  // Pagination State
  const [ticketPage, setTicketPage] = useState(0);
  const [ticketPageSize, setTicketPageSize] = useState(25);
  const [ticketTotalPages, setTicketTotalPages] = useState(0);
  const [ticketTotalItems, setTicketTotalItems] = useState(0);

  // Data States
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState({ total: 0, pending: 0, inProgress: 0, solved: 0, open: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selected Detail View State
  const [selectedTicket, setSelectedTicket] = useState(null);

  const fetchTickets = useCallback(async () => {
    if (!hasPermission('RESOLVE_ISSUES')) return;
    if ((ticketFilters.startDate && !ticketFilters.endDate) || (!ticketFilters.startDate && ticketFilters.endDate)) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await vendorService.getAdminSupportTickets(ticketFilters, ticketPage, ticketPageSize);
      if (res.success && res.data) {
        setTickets(res.data.tickets || []);
        setTicketTotalPages(res.data.totalPages || 0);
        setTicketTotalItems(res.data.totalItems || 0);
        if (res.data.stats) {
          setTicketStats(res.data.stats);
        }
      } else {
        setError(res.error || 'Failed to fetch tickets');
      }
    } catch (err) {
      setError('An error occurred while fetching support tickets.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, ticketFilters, ticketPage, ticketPageSize]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleUpdateTicketStatus = async (id, status) => {
    setError('');
    try {
      const res = await vendorService.updateSupportTicketStatus(id, status);
      if (res.success) {
        toast.success(`Ticket status marked as ${status.replace('_', ' ').toLowerCase()} successfully.`);
        setSelectedTicket(null);
        fetchTickets();
      } else {
        toast.error(res.error || 'Failed to update ticket status.');
      }
    } catch (err) {
      toast.error('An error occurred while updating ticket status.');
      console.error(err);
    }
  };

  const handleViewTicket = (t) => {
    setSelectedTicket(t);
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-24 select-none">
        <div className="w-9 h-9 border-4 border-[#2ab391] border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-sm text-gray-500 font-semibold mt-3">Loading support tickets...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 text-left">
      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200/50 p-4 rounded-xl mb-6 flex items-center gap-3 shadow-sm select-none">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Customer & Vendor Tickets</h2>
        </div>

        {/* Small stats badges */}
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 select-none">
          <span className="px-2 py-1 text-xs font-bold rounded-lg bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap flex-shrink-0">
            Total: {ticketStats.total}
          </span>
          <span className="px-2 py-1 text-xs font-bold rounded-lg bg-amber-50 text-amber-700 border border-amber-200/50 whitespace-nowrap flex-shrink-0">
            Pending: {ticketStats.pending}
          </span>
          <span className="px-2 py-1 text-xs font-bold rounded-lg bg-blue-50 text-blue-700 border border-blue-200/50 whitespace-nowrap flex-shrink-0">
            In Progress: {ticketStats.inProgress}
          </span>
          <span className="px-2 py-1 text-xs font-bold rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200/50 whitespace-nowrap flex-shrink-0">
            Solved: {ticketStats.solved}
          </span>
        </div>
      </div>

      {/* Sub tabs */}
      <div className="flex border-b border-gray-100 mb-6 select-none">
        <button
          onClick={() => {
            setTickets([]);
            setTicketFilters(prev => ({ ...prev, ticketType: 'SUPPORT' }));
            setTicketPage(0);
          }}
          className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all cursor-pointer focus:outline-none ${
            (!ticketFilters.ticketType || ticketFilters.ticketType === 'SUPPORT')
              ? 'border-[#2ab391] text-[#2ab391]'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          Support Tickets
        </button>
        <button
          onClick={() => {
            setTickets([]);
            setTicketFilters(prev => ({ ...prev, ticketType: 'CATEGORY_REQUEST' }));
            setTicketPage(0);
          }}
          className={`pb-3 px-4 text-sm font-bold border-b-2 transition-all cursor-pointer focus:outline-none ${
            ticketFilters.ticketType === 'CATEGORY_REQUEST'
              ? 'border-[#2ab391] text-[#2ab391]'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          Category Requests
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-end justify-between p-4 mb-6 border border-gray-100 bg-gray-50/50 rounded-xl select-none">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 w-full lg:w-auto flex-grow max-w-4xl text-left">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-555 font-bold uppercase tracking-wide">Search</label>
            <input
              type="text"
              placeholder="Search details..."
              value={ticketFilters.search || ''}
              onChange={(e) => {
                setTicketFilters(prev => ({ ...prev, search: e.target.value }));
                setTicketPage(0);
              }}
              className="bg-white border border-gray-205 rounded-lg py-1.5 px-3 text-xs font-semibold text-gray-600 focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-555 font-bold uppercase tracking-wide">Status</label>
            <select
              value={ticketFilters.status}
              onChange={(e) => {
                setTicketFilters(prev => ({ ...prev, status: e.target.value }));
                setTicketPage(0);
              }}
              className="bg-white border border-gray-205 rounded-lg py-1.5 px-3 text-xs font-semibold text-gray-600 focus:border-[#2ab391] focus:ring-1 focus:ring-[#2ab391] focus:outline-none cursor-pointer"
            >
              <option value="ALL">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="SOLVED">Solved</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5 col-span-1 sm:col-span-2">
            <label className="text-xs font-medium text-gray-555 font-bold uppercase tracking-wide">Date Range</label>
            <DateRangePicker
              startDate={ticketFilters.startDate}
              endDate={ticketFilters.endDate}
              onChange={(start, end) => {
                setTicketFilters(prev => ({ ...prev, startDate: start, endDate: end }));
                setTicketPage(0);
              }}
              placeholder="Select Date Range"
              className="w-full bg-white hover:bg-slate-50 border-gray-300"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto justify-between lg:justify-end border-t border-gray-100 pt-3 lg:border-t-0 lg:pt-0">
          <button
            onClick={() => {
              setTickets([]);
              setTicketFilters(prev => ({ ...prev, status: 'ALL', startDate: '', endDate: '', search: '' }));
              setTicketPage(0);
            }}
            className="w-full sm:w-auto px-4 py-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-semibold transition-all active:scale-[0.98] text-center cursor-pointer shadow-sm"
          >
            Clear Filters
          </button>
          <span className="text-xs text-gray-400 font-medium font-sans text-center sm:text-right w-full sm:w-auto">
            Showing {tickets.length} of {ticketTotalItems} tickets
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto w-full">
        <table className="w-full min-w-[980px] border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider text-left">
              <th className="p-4 pl-6 whitespace-nowrap min-w-[130px]">Date</th>
              <th className="p-4 min-w-[200px]">Vendor Details</th>
              <th className="p-4 whitespace-nowrap">Plan</th>
              {ticketFilters.ticketType === 'CATEGORY_REQUEST' ? (
                <>
                  <th className="p-4 min-w-[180px]">Requested Category</th>
                  <th className="p-4 min-w-[220px]">Description / Reason</th>
                </>
              ) : (
                <>
                  <th className="p-4 min-w-[220px]">Query Description</th>
                  <th className="p-4 whitespace-nowrap">Attachment</th>
                </>
              )}
              <th className="p-4 text-center whitespace-nowrap min-w-[120px]">Status</th>
              <th className="p-4 pr-6 text-right whitespace-nowrap min-w-[160px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm text-gray-600">
            {tickets.length === 0 ? (
              <tr>
                <td colSpan={ticketFilters.ticketType === 'CATEGORY_REQUEST' ? 7 : 7} className="p-12 text-center text-gray-400 font-semibold">
                  No support tickets matches the current filter settings.
                </td>
              </tr>
            ) : (
              tickets.map(t => (
                <tr key={t.id} className="hover:bg-gray-50 transition-all border-b border-gray-100 last:border-0">
                  <td className="p-4 pl-6 text-xs text-gray-700 font-semibold text-left whitespace-nowrap min-w-[130px]">
                    <div>{new Date(t.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</div>
                    <div className="text-[10px] mt-0.5 text-gray-500 font-medium">{new Date(t.created_at).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</div>
                  </td>
                  <td className="p-4 text-left min-w-[200px]">
                    <div className="font-semibold text-gray-800">{t.vendor_name}</div>
                    <div className="text-xs text-gray-555 font-bold mt-0.5">{t.vendor_number}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{t.user_email}</div>
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded uppercase ${t.plan_name === 'PREMIUM' ? 'bg-violet-100 text-violet-700' :
                      t.plan_name === 'PLATINUM' ? 'bg-blue-100 text-blue-700' :
                        t.plan_name === 'GOLD' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                      }`}>
                      {t.plan_name}
                    </span>
                  </td>
                  {ticketFilters.ticketType === 'CATEGORY_REQUEST' ? (
                    <>
                      <td className="p-4 text-left whitespace-nowrap">
                        <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-teal-50 text-teal-700 border border-teal-100/50">
                          {t.requested_category}
                        </span>
                      </td>
                      <td className="p-4 max-w-xs min-w-[220px] text-left">
                        <div className="text-xs text-gray-700 line-clamp-2 whitespace-pre-wrap leading-relaxed font-medium">
                          {t.description}
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-4 max-w-xs min-w-[220px] text-left">
                        <div className="text-xs text-gray-700 line-clamp-2 whitespace-pre-wrap leading-relaxed font-medium">
                          {t.description}
                        </div>
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        {t.photo_url ? (
                          <div
                            onClick={() => setLightboxImage(getImageUrl(t.photo_url))}
                            className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 p-0.5 bg-white hover:border-[#2ab391] transition-all cursor-pointer flex items-center justify-center relative group shadow-sm"
                          >
                            <img
                              src={getImageUrl(t.photo_url)}
                              alt="Screenshot thumbnail"
                              className="w-full h-full object-cover rounded"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic font-medium select-none">None</span>
                        )}
                      </td>
                    </>
                  )}
                  <td className="p-4 text-center whitespace-nowrap min-w-[120px]">
                    <span className={`px-2.5 py-1 text-[11px] font-semibold rounded-full uppercase whitespace-nowrap inline-block ${t.status === 'SOLVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                      t.status === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-700 border border-blue-100' :
                        'bg-amber-50 text-amber-700 border border-amber-100'
                      }`}>
                      {t.status === 'SOLVED' ? 'Solved' : t.status === 'IN_PROGRESS' ? 'In Progress' : 'Pending'}
                    </span>
                  </td>
                  <td className="p-4 pr-6 text-right whitespace-nowrap min-w-[160px]">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => handleViewTicket(t)}
                        className="text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 font-bold text-xs py-1.5 px-3 rounded-lg border border-gray-200 transition-all active:scale-[0.98] cursor-pointer shadow-sm"
                      >
                        View
                      </button>
                      <select
                        value={t.status}
                        onChange={(e) => handleUpdateTicketStatus(t.id, e.target.value)}
                        className="bg-white border border-gray-200 hover:border-gray-300 rounded-lg py-1.5 px-2 text-xs font-semibold text-gray-600 focus:border-[#2ab391] focus:outline-none cursor-pointer shadow-sm"
                      >
                        <option value="PENDING">Pending</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="SOLVED">Solved</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-500 font-medium border-t border-gray-100 pt-5 mt-6 select-none bg-gray-50/40">
        <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
          {ticketTotalItems === 0 ? (
            'No records available'
          ) : (
            `Showing ${ticketPage * ticketPageSize + 1} to ${Math.min((ticketPage + 1) * ticketPageSize, ticketTotalItems)} of ${ticketTotalItems} entries`
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
          <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
            <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Show</span>
            <div className="relative">
              <select
                value={ticketPageSize}
                onChange={(e) => {
                  setTicketPageSize(Number(e.target.value));
                  setTicketPage(0);
                }}
                className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                <ChevronDown size={12} />
              </span>
            </div>
            <span className="text-xs text-gray-555 font-bold uppercase tracking-wider">Entries</span>
          </div>

          {ticketTotalPages > 0 && (
            <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setTicketPage(p => Math.max(0, p - 1))}
                disabled={ticketPage === 0}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Previous
              </button>

              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers(ticketPage, ticketTotalPages).map((p, idx) => {
                  if (p === 'ellipsis1' || p === 'ellipsis2') {
                    return <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>;
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => setTicketPage(p)}
                      className={`w-8 h-8 rounded-xl font-bold text-xs flex items-center justify-center transition-all cursor-pointer ${
                        ticketPage === p
                          ? 'bg-[#2ab391] text-white shadow-md shadow-[#2ab391]/10'
                          : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {p + 1}
                    </button>
                  );
                })}
              </div>

              <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm min-w-[70px] text-center">
                {ticketPage + 1} / {ticketTotalPages}
              </span>

              <button
                type="button"
                onClick={() => setTicketPage(p => Math.min(ticketTotalPages - 1, p + 1))}
                disabled={ticketPage >= ticketTotalPages - 1}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm flex-1 sm:flex-initial text-center"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ticket Details Lightbox Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
          <div className="bg-white max-w-lg w-full rounded-2xl shadow-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150 flex flex-col max-h-[90vh] my-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 flex-shrink-0 select-none">
              <div>
                <span className="text-[10px] uppercase font-bold text-gray-400">Support Ticket Detail</span>
                <h3 className="text-lg font-bold text-gray-800 mt-0.5">Ticket #{selectedTicket.id}</h3>
              </div>
              <button onClick={() => setSelectedTicket(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 text-left font-sans">
              <div className="grid grid-cols-2 gap-4 mb-6 bg-gray-50/70 p-4 rounded-xl border border-gray-100 select-none">
                <div>
                  <span className="block text-[9px] uppercase text-gray-400 font-bold">Vendor Name</span>
                  <span className="text-gray-800 font-bold">{selectedTicket.vendor_name}</span>
                </div>
                <div>
                  <span className="block text-[9px] uppercase text-gray-400 font-bold">Contact Email</span>
                  <span className="text-gray-800 font-medium text-xs break-all">{selectedTicket.user_email}</span>
                </div>
                <div>
                  <span className="block text-[9px] uppercase text-gray-400 font-bold">Phone Number</span>
                  <span className="text-gray-800 font-mono text-xs">{selectedTicket.vendor_number}</span>
                </div>
                <div>
                  <span className="block text-[9px] uppercase text-gray-400 font-bold">Membership Plan</span>
                  <span className="text-gray-800 font-bold uppercase">{selectedTicket.plan_name}</span>
                </div>
                {selectedTicket.ticket_type === 'CATEGORY_REQUEST' && (
                  <div className="col-span-2">
                    <span className="block text-[9px] uppercase text-gray-400 font-bold">Requested Category Name</span>
                    <span className="px-2.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-100/50 text-xs font-bold inline-block mt-1">
                      {selectedTicket.requested_category}
                    </span>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-xl text-gray-600 leading-relaxed text-sm mb-6 border border-gray-100 whitespace-pre-wrap font-medium">
                {selectedTicket.ticket_type === 'CATEGORY_REQUEST' ? (
                  <div>
                    <span className="block text-[10px] font-bold text-gray-550 uppercase tracking-wide mb-1">Reason/Description:</span>
                    {selectedTicket.description}
                  </div>
                ) : (
                  selectedTicket.description
                )}
              </div>

              {selectedTicket.photo_url && (
                <div className="mb-6">
                  <span className="block text-[9px] uppercase text-gray-400 font-bold mb-2">Screenshot Attachment</span>
                  <div className="relative group max-w-xs rounded-xl overflow-hidden border border-gray-200 p-1 bg-white hover:border-[#2ab391] transition-all flex items-center justify-center cursor-zoom-in">
                    <img
                      src={getImageUrl(selectedTicket.photo_url)}
                      alt="Ticket screenshot"
                      className="w-full h-auto object-contain rounded-lg max-h-60"
                      onClick={() => setLightboxImage(getImageUrl(selectedTicket.photo_url))}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-between items-center gap-3 bg-gray-50/50 flex-shrink-0 select-none">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-500 uppercase">Status:</span>
                <select
                  value={selectedTicket.status}
                  onChange={(e) => handleUpdateTicketStatus(selectedTicket.id, e.target.value)}
                  className="bg-white border border-gray-200 rounded-lg py-1.5 px-2 text-xs font-bold text-gray-600 focus:border-[#2ab391] focus:outline-none cursor-pointer"
                >
                  <option value="PENDING">Pending</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="SOLVED">Solved</option>
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedTicket(null)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  Close
                </button>
                {selectedTicket.status !== 'SOLVED' && (
                  <button
                    type="button"
                    onClick={() => handleUpdateTicketStatus(selectedTicket.id, 'SOLVED')}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all cursor-pointer shadow-sm shadow-emerald-600/10"
                  >
                    Mark Solved
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicketsTab;
